import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getDiscountList,
  getDisplayableDiscountProperties
} from '../services/discountListService';

const PAGE_SIZE = 6;

export default function EligiblePropertiesGallery() {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [properties, setProperties] = useState([]);
  const [status, setStatus] = useState('loading');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const loadProperties = async () => {
      setStatus('loading');
      try {
        const document = await getDiscountList({
          siteId: SITE_CONFIG.SITE_ID,
          signal: controller.signal
        });
        const listings = getDisplayableDiscountProperties(document);
        setProperties(listings);
        setPage(0);
        setStatus(listings.length ? 'ready' : 'empty');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading eligible properties:', error);
          setStatus('error');
        }
      }
    };

    loadProperties();
    return () => controller.abort();
  }, []);

  const copy = isSpanish ? {
    loading: 'Cargando viviendas elegibles…',
    empty: 'La lista de viviendas elegibles se está actualizando. Comuníquese con Giovanni para conocer las opciones actuales.',
    error: 'Las viviendas elegibles no están disponibles en este momento.',
    eligible: 'Elegible sin pago inicial',
    view: 'Ver propiedad',
    beds: 'hab.',
    baths: 'baños',
    days: 'días en mercado',
    price: 'Precio a consultar',
    previous: 'Propiedades anteriores',
    next: 'Propiedades siguientes',
    photo: 'Foto próximamente'
  } : {
    loading: 'Loading eligible homes…',
    empty: 'The eligible-home list is being updated. Contact Giovanni for current options.',
    error: 'Eligible homes are unavailable right now.',
    eligible: 'No-money-down eligible',
    view: 'View property',
    beds: 'beds',
    baths: 'baths',
    days: 'days on market',
    price: 'Price upon request',
    previous: 'Previous properties',
    next: 'Next properties',
    photo: 'Photo coming soon'
  };

  const pageCount = Math.ceil(properties.length / PAGE_SIZE);
  const visibleProperties = useMemo(
    () => properties.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [page, properties]
  );

  const formatPrice = (price) => {
    if (price == null) return copy.price;
    return new Intl.NumberFormat(isSpanish ? 'es-US' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(Number(price));
  };

  if (status !== 'ready') {
    return (
      <div className={`gt-eligible-status gt-eligible-status--${status}`} role="status">
        {status === 'loading' && <span aria-hidden="true" />}
        <p>{status === 'loading' ? copy.loading : status === 'error' ? copy.error : copy.empty}</p>
      </div>
    );
  }

  return (
    <div className="gt-eligible-properties">
      <div className="gt-eligible-grid">
        {visibleProperties.map((property) => (
          <Link
            className="gt-eligible-card"
            key={property.listingKey}
            to={`/listing/${property.listingKey}`}
            aria-label={`${copy.view}: ${property.address}`}
          >
            {property.imageUrl ? (
              <img src={property.imageUrl} alt={property.address} loading="lazy" decoding="async" />
            ) : (
              <span className="gt-eligible-photo-placeholder" aria-label={copy.photo}>HIG</span>
            )}
            <span className="gt-eligible-card-shade" aria-hidden="true" />
            <span className="gt-eligible-badge">{copy.eligible}</span>
            <div className="gt-eligible-card-copy">
              <strong>{formatPrice(property.price)}</strong>
              <h3>{property.address}</h3>
              <div>
                {property.bedrooms != null && <span>{property.bedrooms} {copy.beds}</span>}
                {property.bathrooms != null && <span>{property.bathrooms} {copy.baths}</span>}
                {property.daysOnMarket != null && <span>{property.daysOnMarket} {copy.days}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="gt-eligible-pagination">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={page === 0}
            aria-label={copy.previous}
          >
            ←
          </button>
          <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, properties.length)} / {properties.length}</span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
            disabled={page === pageCount - 1}
            aria-label={copy.next}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
